// TelcoTrack — Gantt auto-planning: crew defaults, unit rates, activity → phase mapping.
//
// Offset anchor: ±2 weeks = **14 calendar days** from construction start; phase duration
// spans only **Mon–Fri working days** (weekends skipped).
//
// Default kopáči count = 10 → 10 × 28 m/day = 280 m/day (previous absolute rate).

import type { ConstructionPhaseCategory } from './polygon-business.constants';

export const GANTT_PLANNING_CREW_KEYS = ['kopaci', 'montaznici', 'zafukari'] as const;
export type GanttPlanningCrewKey = (typeof GANTT_PLANNING_CREW_KEYS)[number];

/** Per-polygon crew counts (`metadata.ganttPlanning`). */
export interface PolygonGanttPlanningCrewCounts {
  kopaci?: number;
  montaznici?: number;
  zafukari?: number;
}

export const DEFAULT_GANTT_PLANNING_CREW_COUNTS: Required<PolygonGanttPlanningCrewCounts> = {
  kopaci: 10,
  montaznici: 1,
  zafukari: 1,
};

/** Unit throughput per single worker per working day. */
export const GANTT_PLANNING_UNIT_RATES = {
  /** Jeden kopáč — 28 m/den. */
  kopacMetersPerDay: 28,
  montazZavrtPerDay: 5,
  montazOdbockaPerDay: 10,
  montazRozvadecPerDay: 1,
  /** Záfukář — primární nebo sekundární OK. */
  zafukPrimOrSekOkMetersPerDay: 500,
  /** Záfukář — TS (mikrotrubičky). */
  zafukTsMetersPerDay: 330,
} as const;

/** Calendar-day offset from construction start to activity anchor (before snapping to working day). */
export const GANTT_PLANNING_OFFSET_CALENDAR_DAYS = 14;

export const GANTT_PLANNING_ACTIVITY_IDS = [
  'zavrt',
  'zafuk_ts',
  'vykop',
  'zafuk_prim_ok',
  'zafuk_sek_ok',
  'odbocka',
  'rozvadec',
] as const;

export type GanttPlanningActivityId = (typeof GANTT_PLANNING_ACTIVITY_IDS)[number];

export const GANTT_PLANNING_ACTIVITY_LABELS: Record<GanttPlanningActivityId, string> = {
  zavrt: 'Montáž závrtů',
  zafuk_ts: 'Záfuk TS',
  vykop: 'Výkop',
  zafuk_prim_ok: 'Záfuk primárních OK',
  zafuk_sek_ok: 'Záfuk sekundárních OK',
  odbocka: 'Montáž odboček',
  rozvadec: 'Montáž rozvaděče',
};

export const GANTT_ACTIVITY_PHASE_CATEGORY: Record<GanttPlanningActivityId, ConstructionPhaseCategory> = {
  zavrt: 'MONTAZ_OPTIKY',
  odbocka: 'MONTAZ_OPTIKY',
  rozvadec: 'MONTAZ_OPTIKY',
  vykop: 'VYKOPY_POKLADKA',
  zafuk_ts: 'ZAFUK_MT',
  zafuk_prim_ok: 'ZAFUK_OK',
  zafuk_sek_ok: 'ZAFUK_OK',
};

/** Keywords for matching existing phases (categoryCustom, note, section). */
export const GANTT_ACTIVITY_PHASE_HINTS: Record<GanttPlanningActivityId, readonly string[]> = {
  zavrt: ['závr', 'zavrt'],
  odbocka: ['odboč', 'odbock', 'spojkovišt'],
  rozvadec: ['rozvaděč', 'rozvadec'],
  vykop: ['výkop', 'vykop'],
  zafuk_ts: ['ts', 'mikrotrub'],
  zafuk_prim_ok: ['prim'],
  zafuk_sek_ok: ['sekund'],
};

export const GANTT_PLANNING_MIN_CREW_COUNT = 1;
export const GANTT_PLANNING_MAX_CREW_COUNT = 999;

/** Manual plan row on top of the capacity baseline (`metadata.ganttPlanOverrides`). */
export interface GanttActivityPlanOverride {
  /** `dd.mm.yyyy`. */
  plannedFrom?: string;
  /** `dd.mm.yyyy`. */
  plannedTo?: string;
  label?: string;
  /** Lower draws earlier. Absent = canonical activity order. */
  sortOrder?: number;
  editedBy?: string;
  editedAt?: string;
}

export const GANTT_PLAN_OVERRIDE_LABEL_MAX_LENGTH = 120;
export const GANTT_PLAN_OVERRIDE_SORT_MIN = 0;
export const GANTT_PLAN_OVERRIDE_SORT_MAX = 999;
